import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { canFetchWithoutCart, clearRecommendations, fetchRecommendations } from "../engine";
import { isSuggestiveSellingAvailable } from "../selectors";

/**
 * `useRecommendationsSession` — the LIFECYCLE half of the flow: fetch on arrival, clear on
 * departure.
 *
 * Mount this once per ordering surface, ABOVE anything that unmounts while the surface is still
 * open. On the POS that is the New Order *container*, not the screen: the screen remounts while
 * the channel item map is prepared, and a fetch tied to it fired twice per visit.
 *
 * ## Why only V2 fetches on arrival
 *
 * V1 is asked "what goes with THIS item" and has nothing to answer on an empty screen, so it is
 * driven entirely by cart changes (see the sale-update middleware). V2 is asked about the guest
 * and the cart together and can answer on arrival — which is the whole point of it: the operator
 * sees suggestions before the first tap.
 *
 * ## Why the gate is a ref
 *
 * "Already fetched for this visit" has to survive re-renders AND child remounts, and must reset
 * when the operator leaves. State would re-trigger the effect; a ref reset on deactivation is
 * exactly the semantics wanted.
 */
export interface UseRecommendationsSessionOptions {
  /** Is the ordering surface the active screen right now? */
  isActive: boolean;
  /**
   * Is it ready to be asked — catalogue fetched, channel map built, local state initialised?
   * Fetching before this shows suggestions the catalogue would reject anyway.
   */
  isReady?: boolean;
}

export function useRecommendationsSession(options: UseRecommendationsSessionOptions) {
  const { isActive, isReady = true } = options;
  const dispatch = useDispatch();

  const isAvailable = useSelector(isSuggestiveSellingAvailable);
  const saleData = useSelector((state: any) => state.saleDataStore?.saleData);
  const customerStore = useSelector((state: any) => state.customerStore);

  // Read through refs so the fetch effect depends on the GATES only — re-running it on every cart
  // keystroke is the middleware's job, not this hook's.
  const saleDataRef = useRef(saleData);
  saleDataRef.current = saleData;
  const customerStoreRef = useRef(customerStore);
  customerStoreRef.current = customerStore;

  const hasFetchedForVisitRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      // Left the surface — arm the gate for the next visit and drop stale suggestions.
      hasFetchedForVisitRef.current = false;
      clearRecommendations(dispatch);
      return;
    }
    if (!canFetchWithoutCart() || !isAvailable || !isReady || hasFetchedForVisitRef.current) {
      return;
    }
    hasFetchedForVisitRef.current = true;
    fetchRecommendations(dispatch, {
      saleData: saleDataRef.current,
      customerStore: customerStoreRef.current,
    });
  }, [isActive, isAvailable, isReady, dispatch]);

  // Teardown (logout, shell shutdown) must not leave suggestions behind for the next session.
  useEffect(() => {
    return () => {
      clearRecommendations(dispatch);
    };
  }, [dispatch]);
}
